import { useCallback, useState } from "react";

import { Icon } from "./Icon.js";
import { VideoPlayer } from "./VideoPlayer.js";
import type { ClusterState } from "../api/telemetry.js";
import { useI18n } from "../i18n.js";

/** I campi che `clusters.list` fa uscire davvero. La allowlist sta nel
 *  server: qui si dichiara solo quello che si usa. */
export interface Cluster {
  id: string;
  name: string;
  ptzModel?: string | null;
  areaCode?: string | null;
  status?: string | null;
  thresholdL1?: number | null;
  thresholdL2?: number | null;
  currentPpm?: number | null;
  isOutOfService?: number;
  outOfServiceBy?: string | null;
  outOfServiceReason?: string | null;
  plantImageUrl?: string | null;
  cameraIp?: string | null;
  cameraProtocol?: string | null;
  /** Dove passa davvero il controllo PTZ: su ONVIF e' l'IP della camera, su
   *  Pelco-D via NPort e' `ptzIp:ptzPort`, altrimenti la seriale diretta. La
   *  riga di configurazione lo dichiara, perche' e' la prima cosa che si
   *  guarda quando la testa non si muove. */
  ptzIp?: string | null;
  ptzPort?: number | null;
  ptzTransportMode?: string | null;
  sensorIp?: string | null;
  moxaIp?: string | null;
  /** Traccia di ronda attiva: da li' si leggono i waypoint, e dai waypoint si
   *  capisce se il percorso e' un giro completo o un ventaglio. */
  activePtzTraceId?: string | null;
  windEnabled?: number | boolean | null;
  windSensorIp?: string | null;
  /** Quanto dura il rientro a casa prima della ronda, in millisecondi. */
  ptzHomeDurationMs?: number | null;
  /** Geometria del fascio sulla pianta, normalizzata 0-1 sull'immagine. */
  sensorX?: number | null;
  sensorY?: number | null;
  laserEndX?: number | null;
  laserEndY?: number | null;
  /** Geometria del marker laser sul video, in metri e gradi. */
  laserVideoMarkerEnabled?: number | boolean | null;
  laserBaseB?: number | null;
  laserVOff?: number | null;
  laserBoresightH?: number | null;
  laserBoresightV?: number | null;
  laserSideSign?: number | null;
  laserTargetDist?: number | null;
  cameraFovWide?: number | null;
  cameraFovTele?: number | null;
}

export interface DeviceProbe {
  name: string;
  camera: string;
  sensor: string;
  ptz: string;
}

type Status = "NORMAL" | "WARNING" | "CRITICAL" | "OFFLINE";

/**
 * Una scheda per impianto, portata da `ClusterCard.qml`.
 *
 * **E' un riquadro video**, non una scheda di testo: l'immagine dal vivo
 * riempie la card, una sfumatura scurisce il fondo, e sopra ci stanno la
 * pillola LIVE in alto a destra e il piede con nome, area, stato e ppm. Il
 * passaggio del mouse scopre il bottone MODALITA' FOCUS.
 *
 * Quale dei tre apparati non risponde **non si legge qui**: il `.qml` mette
 * nella scheda soltanto video e piede, e il dettaglio per apparato sta dove
 * serve — la barra di stato dell'impianto singolo e il riquadro dell'accesso.
 * Tre pastiglie in piu' cambiano la sagoma della scheda, che e' l'oggetto
 * principale di questa schermata.
 *
 * Regola presa dal porting e da rispettare: **niente numeri che non vengano
 * dal backend**. Un valore di comodo al posto di un dato mancante — uno zero,
 * un trattino trasformato in 0 ppm — dice all'operatore che il sensore legge
 * zero, che e' l'informazione opposta a "il sensore non risponde".
 */
export function ClusterCard({
  cluster,
  telemetry,
  probe,
  streamUrl,
  onOpen,
}: {
  cluster: Cluster;
  telemetry?: ClusterState | undefined;
  probe?: DeviceProbe | undefined;
  /** Percorso HLS sul nostro server, se questo impianto ha una camera. */
  streamUrl?: string | undefined;
  onOpen?: (() => void) | undefined;
}) {
  const { t } = useI18n();
  const [hover, setHover] = useState(false);
  // Non "c'e' un URL", ma "stanno arrivando fotogrammi": un rettangolo nero
  // con scritto LIVE sopra e' una bugia, e su una schermata di sorveglianza
  // e' la bugia peggiore.
  const [playing, setPlaying] = useState(false);
  const onPlayingChange = useCallback((v: boolean) => setPlaying(v), []);

  const status = resolveStatus(cluster, telemetry, probe);
  const ppm = telemetry?.ppm ?? cluster.currentPpm ?? undefined;
  const knowPpm = status !== "OFFLINE" && ppm !== undefined;
  const cls = status.toLowerCase();

  return (
    <article
      className={`tile tile--${cls}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onOpen}
    >
      <div className="tile__video">
        <VideoPlayer src={streamUrl} onPlayingChange={onPlayingChange} />
        <div className="tile__scrim" />

        {/* Pillola LIVE / OFFLINE, in alto a destra come nel QML. */}
        <span className="tile__live">
          <i className={playing ? "dot dot--live" : "dot"} />
          {playing ? t("card.live") : t("focus.offline")}
        </span>

        {onOpen && hover && (
          <div className="tile__hover">
            <span className="cta">
              <Icon name="maximize-2" size={14} />
              {t("card.focusMode")}
            </span>
          </div>
        )}
      </div>

      <footer className="tile__foot">
        <div className="tile__id">
          <div className="tile__name">{cluster.name || "—"}</div>
          <div className="tile__area">
            <Icon name="activity" size={10} />
            {cluster.areaCode ?? ""}
          </div>
        </div>

        <span className={`badge badge--${cls}`}>{status}</span>

        <div className="tile__reading">
          {/* "--" e non "0": un sensore che non risponde non sta leggendo zero. */}
          <div className="tile__ppm">{knowPpm ? Math.round(ppm) : "--"}</div>
          <div className="tile__unit">PPM·M</div>
        </div>
      </footer>

    </article>
  );
}

/**
 * Lo stato che si mostra, in ordine di precedenza.
 *
 * L'apparato irraggiungibile vince su tutto: se il sensore non risponde, il
 * "NORMAL" dell'ultima lettura riuscita e' la bugia piu' pericolosa che questa
 * schermata possa dire.
 */
export function resolveStatus(
  cluster: Cluster,
  telemetry?: ClusterState,
  probe?: DeviceProbe,
): Status {
  if (probe?.sensor === "offline") return "OFFLINE";
  if (telemetry?.sensorOnline === false) return "OFFLINE";
  if (telemetry?.status) return telemetry.status;
  const saved = cluster.status?.toUpperCase();
  if (saved === "NORMAL" || saved === "WARNING" || saved === "CRITICAL") return saved;
  // Nessuna telemetria e nessuno stato salvato: non si inventa "NORMAL".
  return "OFFLINE";
}

/**
 * Impianto **non configurato**, come lo definisce `Home.qml`: manca la
 * camera, manca il sensore, o manca la strada per il PTZ.
 *
 * Serve al contatore dei normali. Il PTZ e' l'unico caso storto: su NPort il
 * comando va all'indirizzo del convertitore seriale, in tutti gli altri modi
 * viaggia verso la camera — quindi senza `cameraIp` non c'e' PTZ nemmeno
 * quando `ptzIp` e' scritto.
 */
export function isUnconfigured(c: Cluster): boolean {
  const hasCamera = !!c.cameraIp;
  const hasSensor = !!c.sensorIp;
  const hasPtz = c.ptzTransportMode === "nport" ? !!c.ptzIp : !!c.cameraIp;
  return !hasCamera || !hasSensor || !hasPtz;
}
